import React, { useCallback, useEffect, useState } from 'react';
import {
    getContentCategories,
    createContentCategory,
    updateContentCategory,
    deleteContentCategory,
    reorderContentCategories,
} from '../../api/contentCategories';

/**
 * 콘텐츠 카테고리 관리 — 추가 / 수정 / 삭제 / 순서 변경
 *
 * 2026-09-23 사용자 요구사항. `cms.categories` 위에서 동작하며 contentType 만 바꾸면
 * 솔루션·제품·사례·노트 어디에나 그대로 쓸 수 있다.
 *
 * 기존 CaseCategoryManager 와 다른 점
 * - 순서를 위/아래 버튼으로 바꾸고 한 번에 저장한다(기존에는 order 를 숫자로 직접 입력했다)
 * - 삭제 시 소속 콘텐츠가 몇 건인지 보여주고, 어디로 옮길지 고르게 한다
 * - 시스템 카테고리('기타')는 삭제 버튼을 감춘다 — 옮길 기본 목적지가 사라지면 안 된다
 * - 서버 오류 메시지를 그대로 보여준다(alert 로 뭉개지 않는다)
 */
const ContentCategoryManager = ({ isOpen, onClose, contentType = 'solution', title = '카테고리 관리', onChanged }) => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [dirty, setDirty] = useState(false);

    const [editId, setEditId] = useState(null);      // number | 'new' | null
    const [draft, setDraft] = useState({ slug: '', label: '', description: '' });
    const [deleting, setDeleting] = useState(null);  // { id, label, contentCount }
    const [reassignTo, setReassignTo] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            setCategories(await getContentCategories(contentType));
            setDirty(false);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [contentType]);

    useEffect(() => {
        if (isOpen) load();
    }, [isOpen, load]);

    if (!isOpen) return null;

    const startAdd = () => {
        setEditId('new');
        setDraft({ slug: '', label: '', description: '' });
        setError('');
    };

    const startEdit = (c) => {
        setEditId(c.id);
        setDraft({ slug: c.slug, label: c.label, description: c.description || '' });
        setError('');
    };

    const cancel = () => {
        setEditId(null);
        setError('');
    };

    const save = async () => {
        setError('');
        try {
            if (editId === 'new') {
                await createContentCategory({ contentType, ...draft });
            } else {
                await updateContentCategory(editId, draft);
            }
            setEditId(null);
            await load();
            onChanged?.();
        } catch (e) {
            setError(e.message);
        }
    };

    /** 위/아래 이동은 로컬 상태만 바꾸고, 저장 버튼을 눌러야 서버에 반영된다. */
    const move = (index, delta) => {
        const next = [...categories];
        const target = index + delta;
        if (target < 0 || target >= next.length) return;
        [next[index], next[target]] = [next[target], next[index]];
        setCategories(next);
        setDirty(true);
    };

    const saveOrder = async () => {
        setError('');
        try {
            setCategories(await reorderContentCategories(contentType, categories.map((c) => c.id)));
            setDirty(false);
            onChanged?.();
        } catch (e) {
            setError(e.message);
        }
    };

    const confirmDelete = async () => {
        setError('');
        try {
            const result = await deleteContentCategory(deleting.id, reassignTo || undefined);
            setDeleting(null);
            setReassignTo('');
            await load();
            onChanged?.();
            if (result?.moved > 0) {
                setError(`삭제했습니다. 콘텐츠 ${result.moved}건을 다른 카테고리로 옮겼습니다.`);
            }
        } catch (e) {
            setError(e.message);
        }
    };

    const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none';

    return (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div className="w-full max-w-3xl max-h-[85vh] flex flex-col bg-white rounded-2xl shadow-xl">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <div>
                        <h2 className="text-lg font-bold text-gray-900">{title}</h2>
                        <p className="text-xs text-gray-500 mt-0.5">순서를 바꾼 뒤에는 &lsquo;순서 저장&rsquo;을 눌러야 반영됩니다.</p>
                    </div>
                    <button type="button" onClick={onClose} aria-label="닫기" className="w-10 h-10 rounded-lg text-gray-500 hover:bg-gray-100">✕</button>
                </div>

                {error && (
                    <div className="mx-6 mt-4 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900 whitespace-pre-line">
                        {error}
                    </div>
                )}

                <div className="flex-1 overflow-y-auto px-6 py-4">
                    {loading ? (
                        <p className="text-sm text-gray-500 py-8 text-center">불러오는 중…</p>
                    ) : (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs font-bold text-gray-500 border-b border-gray-200">
                                    <th className="py-2 w-20">순서</th>
                                    <th className="py-2">카테고리명</th>
                                    <th className="py-2 w-40">코드 <span className="font-normal text-gray-400">(URL · 고정)</span></th>
                                    <th className="py-2 w-20 text-right">콘텐츠</th>
                                    <th className="py-2 w-32 text-right">관리</th>
                                </tr>
                            </thead>
                            <tbody>
                                {categories.map((c, i) => (
                                    <tr key={c.id} className="border-b border-gray-100">
                                        {editId === c.id ? (
                                            <td colSpan={5} className="py-3">
                                                <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                                                    <input className={inputCls} value={draft.label} placeholder="카테고리명 (예: 유통)" onChange={(e) => setDraft({ ...draft, label: e.target.value })} />
                                                    <input className={`${inputCls} font-mono text-xs text-gray-600`} value={draft.slug} placeholder="retail" disabled={c.isSystem} title="URL에 쓰입니다. 공개한 뒤에는 바꾸지 마세요." onChange={(e) => setDraft({ ...draft, slug: e.target.value })} />
                                                    <div className="flex gap-2">
                                                        <button type="button" onClick={save} className="px-3 py-2 rounded-lg bg-primary-500 text-white text-xs font-bold">저장</button>
                                                        <button type="button" onClick={cancel} className="px-3 py-2 rounded-lg bg-gray-100 text-gray-700 text-xs font-bold">취소</button>
                                                    </div>
                                                </div>
                                            </td>
                                        ) : (
                                            <>
                                                <td className="py-2">
                                                    <div className="flex gap-1">
                                                        <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="위로" className="w-7 h-7 rounded border border-gray-200 text-gray-600 disabled:opacity-30">↑</button>
                                                        <button type="button" onClick={() => move(i, 1)} disabled={i === categories.length - 1} aria-label="아래로" className="w-7 h-7 rounded border border-gray-200 text-gray-600 disabled:opacity-30">↓</button>
                                                    </div>
                                                </td>
                                                <td className="py-2 font-semibold text-gray-900">
                                                    {c.label}
                                                    {c.isSystem && <span className="ml-2 px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 text-[11px] font-bold">기본</span>}
                                                </td>
                                                <td className="py-2 text-gray-500 font-mono text-xs">{c.slug}</td>
                                                <td className="py-2 text-right text-gray-600">{c.contentCount}</td>
                                                <td className="py-2 text-right">
                                                    <button type="button" onClick={() => startEdit(c)} className="px-2 py-1 text-xs font-bold text-gray-700 hover:text-primary-500">수정</button>
                                                    {!c.isSystem && (
                                                        <button type="button" onClick={() => { setDeleting(c); setReassignTo(''); }} className="px-2 py-1 text-xs font-bold text-red-600 hover:text-red-700">삭제</button>
                                                    )}
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                ))}

                                {editId === 'new' && (
                                    <tr className="border-b border-gray-100 bg-primary-50/40">
                                        <td colSpan={5} className="py-3">
                                            <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                                                <input className={inputCls} value={draft.label} placeholder="카테고리명 (예: 유통)" onChange={(e) => setDraft({ ...draft, label: e.target.value })} />
                                                <input className={`${inputCls} font-mono text-xs text-gray-600`} value={draft.slug} placeholder="retail — 영문 소문자·하이픈" title="URL에 쓰입니다. 공개한 뒤에는 바꾸지 마세요." onChange={(e) => setDraft({ ...draft, slug: e.target.value })} />
                                                <div className="flex gap-2">
                                                    <button type="button" onClick={save} className="px-3 py-2 rounded-lg bg-primary-500 text-white text-xs font-bold">추가</button>
                                                    <button type="button" onClick={cancel} className="px-3 py-2 rounded-lg bg-gray-100 text-gray-700 text-xs font-bold">취소</button>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}

                    {/*
                      두 칸을 왜 받는지 관리자가 알아야 실수가 줄어든다.
                      이름을 바꿔도 안 깨지는 이유가 코드를 따로 두었기 때문이다.
                    */}
                    <p className="mt-3 text-xs text-gray-500 leading-relaxed">
                        <b className="text-gray-700">카테고리명</b>은 화면에 보이는 이름으로, 언제든 바꿔도 됩니다.{' '}
                        <b className="text-gray-700">코드</b>는 주소(URL)에 쓰이고 콘텐츠가 실제로 붙잡고 있는 값이라,
                        공개한 뒤에 바꾸면 검색 순위와 기존 링크가 끊어집니다.
                    </p>

                    {deleting && (
                        <div className="mt-4 p-4 rounded-xl border border-red-200 bg-red-50">
                            <p className="text-sm font-bold text-red-900">&lsquo;{deleting.label}&rsquo; 카테고리를 삭제합니다.</p>
                            <p className="text-xs text-red-800 mt-1">
                                {deleting.contentCount > 0
                                    ? `이 카테고리에 속한 콘텐츠 ${deleting.contentCount}건은 삭제되지 않고 아래에서 고른 카테고리로 옮겨집니다.`
                                    : '속한 콘텐츠가 없습니다.'}
                            </p>
                            {deleting.contentCount > 0 && (
                                <select className={`${inputCls} mt-3`} value={reassignTo} onChange={(e) => setReassignTo(e.target.value)}>
                                    <option value="">기본 카테고리(기타)로 옮기기</option>
                                    {categories.filter((c) => c.id !== deleting.id).map((c) => (
                                        <option key={c.id} value={c.id}>{c.label} 로 옮기기</option>
                                    ))}
                                </select>
                            )}
                            <div className="flex gap-2 mt-3">
                                <button type="button" onClick={confirmDelete} className="px-3 py-2 rounded-lg bg-red-600 text-white text-xs font-bold">삭제</button>
                                <button type="button" onClick={() => setDeleting(null)} className="px-3 py-2 rounded-lg bg-white border border-gray-300 text-gray-700 text-xs font-bold">취소</button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
                    <button type="button" onClick={startAdd} disabled={editId !== null} className="px-4 py-2 rounded-lg bg-white border border-gray-300 text-sm font-bold text-gray-700 disabled:opacity-40">+ 카테고리 추가</button>
                    <div className="flex gap-2">
                        {dirty && <button type="button" onClick={saveOrder} className="px-4 py-2 rounded-lg bg-primary-500 text-white text-sm font-bold">순서 저장</button>}
                        <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg bg-gray-200 text-sm font-bold text-gray-700">닫기</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ContentCategoryManager;
